import { useCallback, useEffect, useRef } from 'react';

import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';

import { postNotifications } from '../../axios/Utils/notifications';
import { NotificationPermissionStatus } from '../../types/Tracking/types';
import {
  NOTIFICATION_PERMISSION_EVENTS,
  buildNotificationPermissionResolutionPayload,
  notificationPermissionEvents,
} from '../../utils/notificationPermissionEvents';
import { StorageService } from '../../utils/storage';

const NOTIFICATION_TOKEN_KEY = 'NOTIFICATION_TOKEN';
const NOTIFICATION_PERMISSION_KEY = 'NOTIFICATION_PERMISSION_REQUESTED';
const NOTIFICATION_PERMISSION_DATE_KEY = 'NOTIFICATION_PERMISSION_DATE';
const NOTIFICATION_RENEWAL_DAYS = 30;
const PUSH_NOTIFICATIONS_VERBOSE_LOGS_KEY = 'FP_PUSH_NOTIFICATIONS_VERBOSE_LOGS';
const ANDROID_REGISTRATION_RETRY_DELAYS_MS = [1500, 4000];

const IS_NATIVE = Capacitor.isNativePlatform();
const IS_ANDROID = Capacitor.getPlatform() === 'android';
const PUSH_LOG_CONTEXT = {
  platform: Capacitor.getPlatform(),
};

type NotificationHandler = (notification: PushNotificationSchema) => void;
type ActionHandler = (action: ActionPerformed) => void;

interface UsePushNotificationsOptions {
  onNotificationReceived?: NotificationHandler;
  onNotificationAction?: ActionHandler;
}

const normalizePermissionStatus = (
  status?: string | null,
): NotificationPermissionStatus => {
  switch (status) {
    case 'granted':
      return 'granted';
    case 'denied':
      return 'denied';
    case 'prompt':
      return 'prompt';
    default:
      return 'unknown';
  }
};

const isVerbosePushNotificationsEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_PUSH_NOTIFICATIONS_VERBOSE_LOGS === 'true';
  }

  const storedOverride = window.localStorage.getItem(PUSH_NOTIFICATIONS_VERBOSE_LOGS_KEY);
  if (storedOverride === 'true') return true;
  if (storedOverride === 'false') return false;

  return process.env.REACT_APP_PUSH_NOTIFICATIONS_VERBOSE_LOGS === 'true';
};

export const debugPushNotificationsLog = (...args: unknown[]): void => {
  if (!isVerbosePushNotificationsEnabled()) return;
  console.log(...args);
};

const logPushNotificationsInfo = (message: string, payload?: unknown): void => {
  if (payload === undefined) {
    console.info(`[PushNotifications] ${message}`);
    return;
  }

  console.info(`[PushNotifications] ${message}`, payload);
};

const logPushNotificationsWarning = (message: string, payload?: unknown): void => {
  if (payload === undefined) {
    console.warn(`[PushNotifications] ${message}`);
    return;
  }

  console.warn(`[PushNotifications] ${message}`, payload);
};

export const getStoredNotificationToken = async (): Promise<string | null> => {
  const storedToken = await StorageService.get(NOTIFICATION_TOKEN_KEY);
  debugPushNotificationsLog('Token almacenado:', storedToken);
  return storedToken;
};

export const clearStoredNotificationToken = async (): Promise<void> => {
  await StorageService.remove(NOTIFICATION_TOKEN_KEY);
};

export const markNotificationPermissionPrompted = async (): Promise<void> => {
  await StorageService.set(NOTIFICATION_PERMISSION_KEY, 'true');
  await StorageService.set(NOTIFICATION_PERMISSION_DATE_KEY, new Date().toISOString());
};

export const getNotificationPermissionStatus =
  async (): Promise<NotificationPermissionStatus> => {
    if (!IS_NATIVE) return 'unknown';

    try {
      const result = await PushNotifications.checkPermissions();
      return normalizePermissionStatus(result.receive);
    } catch (error) {
      console.error('[PushNotifications] Error checking permissions:', error);
      return 'unknown';
    }
  };

export const usePushNotifications = (options: UsePushNotificationsOptions = {}) => {
  const { onNotificationReceived, onNotificationAction } = options;
  const isInitialized = useRef(false);
  const hasRegistered = useRef(false);
  const lastSyncedToken = useRef<string | null>(null);
  const androidRegistrationRetryTimeouts = useRef<Array<ReturnType<typeof setTimeout>>>(
    [],
  );

  const onNotificationReceivedRef = useRef(onNotificationReceived);
  const onNotificationActionRef = useRef(onNotificationAction);

  useEffect(() => {
    onNotificationReceivedRef.current = onNotificationReceived;
    onNotificationActionRef.current = onNotificationAction;
  }, [onNotificationReceived, onNotificationAction]);

  const clearAndroidRegistrationRetryTimeouts = useCallback(() => {
    androidRegistrationRetryTimeouts.current.forEach((timeoutId) =>
      clearTimeout(timeoutId),
    );
    androidRegistrationRetryTimeouts.current = [];
  }, []);

  const persistAndSyncToken = useCallback(
    async (rawToken: string | null | undefined, source: string): Promise<boolean> => {
      const token = rawToken?.trim();

      if (!token || token === 'null' || token.length < 20) {
        logPushNotificationsWarning(`Token FCM invalido recibido desde ${source}`, {
          ...PUSH_LOG_CONTEXT,
          source,
          token,
        });
        debugPushNotificationsLog(
          `[PushNotifications] Token invalido desde ${source}:`,
          token,
        );
        return false;
      }

      await StorageService.set(NOTIFICATION_TOKEN_KEY, token);
      logPushNotificationsInfo(`Token FCM recibido desde ${source}`, {
        ...PUSH_LOG_CONTEXT,
        source,
        token,
      });

      if (lastSyncedToken.current === token) {
        clearAndroidRegistrationRetryTimeouts();
        logPushNotificationsInfo('Token FCM ya estaba sincronizado en esta sesion', {
          ...PUSH_LOG_CONTEXT,
          source,
          token,
        });
        debugPushNotificationsLog(
          `[PushNotifications] Token ya sincronizado en esta sesion (${source})`,
        );
        return true;
      }

      debugPushNotificationsLog(
        `[PushNotifications] Token recibido desde ${source}:`,
        token,
      );
      logPushNotificationsInfo('Enviando token FCM al backend', {
        ...PUSH_LOG_CONTEXT,
        source,
        token,
      });

      try {
        const success = await postNotifications(token);

        if (success) {
          lastSyncedToken.current = token;
          clearAndroidRegistrationRetryTimeouts();
          logPushNotificationsInfo('Token FCM sincronizado con backend', {
            ...PUSH_LOG_CONTEXT,
            source,
            token,
          });
          debugPushNotificationsLog(
            `[PushNotifications] Token enviado al backend (${source})`,
          );
          return true;
        }

        logPushNotificationsWarning(
          'El backend no confirmo la sincronizacion del token FCM',
          {
            ...PUSH_LOG_CONTEXT,
            source,
            token,
          },
        );
        debugPushNotificationsLog(
          `[PushNotifications] El backend no confirmo la sincronizacion del token (${source})`,
        );
        return false;
      } catch (err) {
        console.error(`[PushNotifications] Error enviando token (${source}):`, err);
        return false;
      }
    },
    [clearAndroidRegistrationRetryTimeouts],
  );

  const scheduleAndroidRegistrationRetry = useCallback(
    (reason: string) => {
      if (!IS_ANDROID) return;

      clearAndroidRegistrationRetryTimeouts();

      ANDROID_REGISTRATION_RETRY_DELAYS_MS.forEach((delayMs, index) => {
        const attempt = index + 1;
        const timeoutId = setTimeout(() => {
          void (async () => {
            const storedToken = (
              await StorageService.get(NOTIFICATION_TOKEN_KEY)
            )?.trim();

            if (storedToken && storedToken.length >= 20) {
              logPushNotificationsInfo(
                'Android ya tiene token FCM antes del reintento programado',
                {
                  ...PUSH_LOG_CONTEXT,
                  reason,
                  attempt,
                  delayMs,
                  token: storedToken,
                },
              );
              void persistAndSyncToken(storedToken, `android-retry-stored-${attempt}`);
              return;
            }

            logPushNotificationsWarning(
              'Android sigue sin token FCM; reintentando registro',
              {
                ...PUSH_LOG_CONTEXT,
                reason,
                attempt,
                delayMs,
              },
            );

            try {
              await PushNotifications.register();
            } catch (error) {
              console.error(
                '[PushNotifications] Error reintentando el registro Android:',
                {
                  ...PUSH_LOG_CONTEXT,
                  reason,
                  attempt,
                  delayMs,
                  error,
                },
              );
            }
          })();
        }, delayMs);

        androidRegistrationRetryTimeouts.current.push(timeoutId);
      });
    },
    [clearAndroidRegistrationRetryTimeouts, persistAndSyncToken],
  );

  useEffect(() => {
    if (!IS_NATIVE) return undefined;

    const handler = async (event: any) => {
      const token =
        event?.detail?.token || event?.token || event?.detail?.userInfo?.token;
      await persistAndSyncToken(token, 'FCMToken');
    };

    window.addEventListener('FCMToken', handler as EventListener);

    return () => {
      window.removeEventListener('FCMToken', handler as EventListener);
    };
  }, [persistAndSyncToken]);

  const shouldRenewPermissionRequest = useCallback(async (): Promise<boolean> => {
    const lastRequestDate = await StorageService.get(NOTIFICATION_PERMISSION_DATE_KEY);
    if (!lastRequestDate) return true;

    const lastDate = new Date(lastRequestDate);
    const today = new Date();
    const daysPassed = Math.floor(
      (today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    return daysPassed >= NOTIFICATION_RENEWAL_DAYS;
  }, []);

  const hasAskedForPermission = useCallback(async (): Promise<boolean> => {
    const asked = await StorageService.get(NOTIFICATION_PERMISSION_KEY);
    return asked === 'true';
  }, []);

  const getStoredToken = useCallback(async (): Promise<string | null> => {
    return getStoredNotificationToken();
  }, []);

  const clearToken = useCallback(async (): Promise<void> => {
    lastSyncedToken.current = null;
    clearAndroidRegistrationRetryTimeouts();
    await clearStoredNotificationToken();
  }, [clearAndroidRegistrationRetryTimeouts]);

  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    if (!IS_NATIVE) return false;

    try {
      logPushNotificationsInfo(
        'Solicitando permisos de notificaciones',
        PUSH_LOG_CONTEXT,
      );
      const permStatus = await PushNotifications.requestPermissions();
      const normalizedStatus = normalizePermissionStatus(permStatus.receive);
      logPushNotificationsInfo('Estado de permisos resuelto', {
        ...PUSH_LOG_CONTEXT,
        status: normalizedStatus,
      });

      await markNotificationPermissionPrompted();

      notificationPermissionEvents.emit(
        NOTIFICATION_PERMISSION_EVENTS.FLOW_RESOLVED,
        buildNotificationPermissionResolutionPayload(
          normalizedStatus,
          'prompt_result',
          true,
        ),
      );

      if (normalizedStatus === 'granted') {
        logPushNotificationsInfo(
          'Permisos otorgados; registrando dispositivo en FCM',
          PUSH_LOG_CONTEXT,
        );
        await PushNotifications.register();
        if (IS_ANDROID) {
          scheduleAndroidRegistrationRetry('permission-granted');
        }
        debugPushNotificationsLog('[PushNotifications] Permisos otorgados y registrado');
        return true;
      }

      debugPushNotificationsLog('[PushNotifications] Permisos denegados');
      return false;
    } catch (error) {
      console.error('[PushNotifications] Error al solicitar permisos:', error);
      return false;
    }
  }, [scheduleAndroidRegistrationRetry]);

  const initialize = useCallback(async () => {
    if (!IS_NATIVE) return;
    if (isInitialized.current) return;
    isInitialized.current = true;
    logPushNotificationsInfo(
      'Inicializando flujo de push notifications',
      PUSH_LOG_CONTEXT,
    );

    PushNotifications.addListener('registration', (token: Token) => {
      if (!IS_ANDROID) {
        debugPushNotificationsLog(
          '[PushNotifications] Listener registration de Capacitor ignorado en iOS; se mantiene el flujo FCMToken nativo',
        );
        return;
      }

      logPushNotificationsInfo(
        'Listener registration de Capacitor devolvio token Android',
        {
          ...PUSH_LOG_CONTEXT,
          source: 'registration',
          token: token.value,
        },
      );
      void persistAndSyncToken(token.value, 'registration');
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('[PushNotifications] Error de registro:', {
        ...PUSH_LOG_CONTEXT,
        error,
      });
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      logPushNotificationsInfo('Push notification recibida', {
        ...PUSH_LOG_CONTEXT,
        id: notification.id,
        title: notification.title,
      });
      onNotificationReceivedRef.current?.(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      logPushNotificationsInfo('Push notification abierta desde el sistema', {
        ...PUSH_LOG_CONTEXT,
        actionId: action.actionId,
        id: action.notification?.id,
        title: action.notification?.title,
      });
      onNotificationActionRef.current?.(action);
    });

    setTimeout(async () => {
      const token = await StorageService.get(NOTIFICATION_TOKEN_KEY);
      if (token) {
        void persistAndSyncToken(token, 'stored-token-fallback');
      }
    }, 3000);

    if (!hasRegistered.current) {
      hasRegistered.current = true;

      const existingToken = await StorageService.get(NOTIFICATION_TOKEN_KEY);
      if (existingToken && existingToken.length > 10) {
        void persistAndSyncToken(existingToken, 'stored-token-bootstrap');
      }

      const permissionStatus = await getNotificationPermissionStatus();
      logPushNotificationsInfo('Estado actual de permisos al inicializar', {
        ...PUSH_LOG_CONTEXT,
        status: permissionStatus,
      });
      if (permissionStatus === 'granted') {
        try {
          await PushNotifications.register();
          if (IS_ANDROID) {
            scheduleAndroidRegistrationRetry('initialize-permission-granted');
          }
          debugPushNotificationsLog(
            '[PushNotifications] Registro refrescado con permisos previamente otorgados',
          );
        } catch (error) {
          console.error('[PushNotifications] Error refrescando el registro:', error);
        }
        return;
      }

      if (!existingToken) {
        debugPushNotificationsLog(
          '[PushNotifications] Sin permisos/token; esperando solicitud explicita post-login',
        );
      }
    }
  }, [persistAndSyncToken, scheduleAndroidRegistrationRetry]);

  const cleanup = useCallback(async () => {
    if (IS_NATIVE && isInitialized.current) {
      clearAndroidRegistrationRetryTimeouts();
      await PushNotifications.removeAllListeners();
      hasRegistered.current = false;
      isInitialized.current = false;
    }
  }, [clearAndroidRegistrationRetryTimeouts]);

  const isSupported = useCallback(() => IS_NATIVE, []);

  const renewPermissionRequest = useCallback(async (): Promise<boolean> => {
    debugPushNotificationsLog('[PushNotifications] Renovando solicitud de permisos');
    return requestNotificationPermission();
  }, [requestNotificationPermission]);

  return {
    isSupported,
    initialize,
    cleanup,
    getStoredToken,
    clearToken,
    requestNotificationPermission,
    renewPermissionRequest,
    hasAskedForPermission,
    shouldRenewPermissionRequest,
    getNotificationPermissionStatus,
  };
};
